/**
 * Client Request ▸ **Service Scheduling** (design v2.0 §12).
 *
 * The dedicated calendar over the same services the Allocation board lists.
 * Allocation is where dispatch reads and corrects the state of every service;
 * Scheduling is where they lay the week out and move work around it.
 *
 * FOUR VIEWS, and the fourth is the reason this is its own screen:
 *   Day       one column, working hours down the side
 *   Week      seven columns, plus the Unscheduled tray
 *   Month     the whole month, for looking a long way ahead
 *   TIMELINE  ONE ROW PER DRIVER across the week — the only view where a
 *             single drag answers WHEN and WHO together, because its two axes
 *             ARE those two fields.
 *
 * It writes through the SAME audited allocation endpoint the list's date and
 * driver cells use, so a move made by dragging produces the same history as one
 * typed into a row (§11.1). Nothing here can change a service's category,
 * address or answers — the endpoint has no way to express it.
 */
import { useCallback, useEffect, useState } from 'react';
import { navigateToPage } from '../../layouts/navigateTo';
import '../backlog/backlog.css';
import './clientRequest.css';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import ServiceCalendar, { CalViewSwitch, type CalView } from './ServiceCalendar';
import {
  AUDIT_FORM_NAME, allocateService, fetchServiceAllocation,
  fetchServiceDefinitions, fetchTechnicians,
  type ServiceAllocationRow, type ServiceCategoryDef,
} from './clientRequestApi';

/** All four, in this order. Module-level so the array identity is stable —
 *  the switcher and the calendar must be handed the SAME list. */
const VIEWS: CalView[] = ['day', 'week', 'month', 'timeline'];

export default function ServiceSchedulingPage() {
  const [rows,    setRows]    = useState<ServiceAllocationRow[]>([]);
  const [defs,    setDefs]    = useState<ServiceCategoryDef[]>([]);
  const [techs,   setTechs]   = useState<{ id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs,  setLoadMs]  = useState<number>();
  const [busyId,  setBusyId]  = useState<string | null>(null);
  const [view,    setView]    = useState<CalView>('week');

  const notify   = useNotify();

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const [r, d, w] = await Promise.all([
        fetchServiceAllocation(), fetchServiceDefinitions(), fetchTechnicians(),
      ]);
      setRows(r); setDefs(d); setTechs(w);
      setLoadMs(Math.round(performance.now() - t0));
    } catch {
      notify.error('Could not load the schedule.');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  /**
   * The one write this screen makes. `driverId === undefined` means "leave the
   * driver alone" — a drop on a calendar day changes WHEN, not WHO, and a
   * screen that quietly reassigned someone because a card moved would be a bug
   * the audit trail would then faithfully record. Only the Timeline passes a
   * driver, because there the row the card landed in IS the answer to "who".
   */
  const move = async (
    row: ServiceAllocationRow, date: string | null, driverId?: string | null,
  ) => {
    const driver = driverId === undefined ? row.assignedToId : driverId;
    setBusyId(row.id);
    try {
      await allocateService(row.id, date, driver);
      setRows(rs => rs.map(r => r.id === row.id
        ? { ...r,
            scheduledDate: date,
            assignedToId: driver,
            assignedToName: techs.find(t => t.id === driver)?.label ?? null }
        : r));
      notify.success('Saved successfully.');
    } catch (e) {
      notify.error(e instanceof Error ? `Could not move the service: ${e.message}`
                                      : 'Could not move the service.');
      // Re-read, so the calendar shows what is stored rather than the move that
      // failed — a card left sitting where it was dropped would be a lie.
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bl-page">
      <div className="bl-top">
        {/* The SHARED list-page header block — same structure as Request
            Registration and the allocation board, so all three Client Request
            screens open with the same rhythm. */}
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Client Request</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Service Scheduling</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="svcsched" loadMs={loadMs} loading={loading}
                       auditFormName={AUDIT_FORM_NAME} />
            {/* The view switcher sits on the TITLE row (prototype v68), not in
                the calendar's own nav bar — which is why `view` is passed down
                controlled. The nav bar keeps the things that change WITHIN a
                view (prev / next / Today); which view you are in is a property
                of the screen, so it belongs with the screen's title. */}
            <CalViewSwitch views={VIEWS} view={view} onPick={setView} />
          </div>
          <div className="dp-subtitle">
            {view === 'timeline'
              ? 'One row per driver. Drag across to change the date, or down to change the driver.'
              : 'Drag a service to another slot to reschedule it. Every move is audited.'}
          </div>
        </div>

        {/* The calendar card follows immediately, with no filter row between,
            so the toolbar carries the gap itself. */}
        <div className="cr-top-actions">
          <AppToolbar
            left={
              /* Back to the board. The two screens are one dataset read two ways,
                 so the trip between them is a view change, not a context switch —
                 which is why it sits here rather than in the navigation. */
              <button className="ca-btn" onClick={() => navigateToPage('svcaloc')}>
                <span className="tb-i"><i className="ti ti-list" /></span> List view
              </button>
            }
            onRefresh={() => void load()}
            refreshing={loading}
            refreshDisabled={loading}
          />
        </div>
      </div>

      <div className="bl-grid-area">
        {/* sc-host, not sc-card — `.sc-card` is the service CARD inside the
            calendar, and reusing it here would dress this container in a
            draggable card's border and cursor. It marks the card as the
            calendar's scroll host: see clientRequest.css. */}
        <div className="bl-card bl-card-flex sc-host">
          <ServiceCalendar
            rows={rows}
            defs={defs}
            techs={techs}
            busyId={busyId}
            views={VIEWS}
            view={view}
            onViewChange={setView}
            onMove={(row, date) => move(row, date)}
            onAssign={(row, date, driverId) => move(row, date, driverId)}
          />
        </div>
      </div>
    </div>
  );
}
